import React, { useState, useEffect, useMemo, useCallback } from "react";
import PageHero from "../components/dental/PageHero";
import Button from "../components/dental/Button";
import VideoTestimonialsFilmRoll from "../components/dental/VideoTestimonialsFilmRoll";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { SMILE_GALLERY_API, TESTIMONIALS_API } from "../data/site";

const GalleryCard = ({ item, onOpen }) => {
  const slides = (item.images || [])
    .filter((src) => typeof src === "string" && src.trim())
    .map((src, slideIndex) => ({ src, label: `Image ${slideIndex + 1}` }));

  if (!slides.length) {
    return null;
  }

  const lastSlide = slides[slides.length - 1];

  return (
    <div
      className="overflow-hidden rounded-lg shadow-md hover:shadow-lg transition-shadow cursor-pointer group"

      // className="bg-[#ffffff] rounded-lg border   border-[#B79A68]/40 overflow-hidden "
    >
      <div
        className="relative aspect-square  overflow-hidden cursor-zoom-in"
        onClick={() => onOpen?.(item)}
      >
        <img
          src={lastSlide.src}
          alt={`${item.label} ${lastSlide.label}`}
          className="absolute inset-0 w-full h-full object-cover"
        />

        {slides.length > 1 && (
          <div className="absolute bottom-3 right-3 bg-black/65 text-white text-xs tracking-[0.08em] uppercase px-2 py-1">
            {slides.length} Images
          </div>
        )}
      </div>
    </div>
  );
};

const getImageUrls = (item) => {
  if (Array.isArray(item?.imageDetails)) {
    const orderedImageDetails = item.imageDetails
      .filter(
        (entry) =>
          entry &&
          typeof entry === "object" &&
          typeof entry.url === "string" &&
          entry.url.trim(),
      )
      .sort((left, right) => {
        const leftOrder = Number.isFinite(Number(left?.order))
          ? Number(left.order)
          : Number.POSITIVE_INFINITY;
        const rightOrder = Number.isFinite(Number(right?.order))
          ? Number(right.order)
          : Number.POSITIVE_INFINITY;
        return leftOrder - rightOrder;
      })
      .map((entry) => entry.url.trim());

    if (orderedImageDetails.length) return orderedImageDetails;
  }

  const fromArrayKeys = ["images", "galleryImages", "media", "photos"];

  const getImageFromValue = (value) => {
    if (typeof value === "string") {
      return value.trim();
    }

    if (value && typeof value === "object") {
      const objectUrl =
        value.url ||
        value.image ||
        value.imageUrl ||
        value.src ||
        value.path ||
        value.location;

      return typeof objectUrl === "string" ? objectUrl.trim() : "";
    }

    return "";
  };

  for (const key of fromArrayKeys) {
    if (Array.isArray(item?.[key])) {
      const urls = item[key].map(getImageFromValue).filter(Boolean);
      if (urls.length) return urls;
    }
  }

  const singles = [
    item?.before,
    item?.after,
    item?.image,
    item?.imageUrl,
    item?.url,
  ]
    .map(getImageFromValue)
    .filter(Boolean);

  return singles;
};

const normalizeGalleryItem = (item, index) => {
  const images = getImageUrls(item);
  return {
    id: item?._id || item?.id || `gallery-item-${index}`,
    label: item?.label || item?.title || `Smile Case ${index + 1}`,
    images,
    source: item,
  };
};

const getGalleryOrder = (item) => {
  const value = item?.source?.order ?? item?.source?.orderNumber;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : Number.POSITIVE_INFINITY;
};

const getGallerySortTime = (item) => {
  const value =
    item?.source?.createdAt ||
    item?.source?.created_at ||
    item?.source?.date ||
    item?.source?.publishedAt ||
    item?.source?.updatedAt ||
    item?.source?.updated_at ||
    item?.source?.timestamp ||
    item?.source?.time;

  const parsed = value ? new Date(value).getTime() : NaN;
  return Number.isFinite(parsed) ? parsed : Number.POSITIVE_INFINITY;
};

const SmileGallery = () => {
  const [apiGalleryItems, setApiGalleryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePreview, setActivePreview] = useState(null);
  const [activePreviewIndex, setActivePreviewIndex] = useState(0);

  const previewSlides = useMemo(() => {
    if (!activePreview?.item?.images) return [];
    return activePreview.item.images.filter(
      (src) => typeof src === "string" && src.trim(),
    );
  }, [activePreview]);

  useEffect(() => {
    if (!activePreview) {
      setActivePreviewIndex(0);
      return;
    }

    setActivePreviewIndex(
      Math.max(0, (activePreview.item?.images?.length || 1) - 1),
    );
  }, [activePreview]);

  const closePreview = useCallback(() => {
    setActivePreview(null);
  }, []);

  const showPreviousPreview = useCallback(() => {
    setActivePreviewIndex((currentIndex) => {
      if (!previewSlides.length) return currentIndex;
      return (currentIndex - 1 + previewSlides.length) % previewSlides.length;
    });
  }, [previewSlides.length]);

  const showNextPreview = useCallback(() => {
    setActivePreviewIndex((currentIndex) => {
      if (!previewSlides.length) return currentIndex;
      return (currentIndex + 1) % previewSlides.length;
    });
  }, [previewSlides.length]);

  useEffect(() => {
    if (!activePreview) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closePreview();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activePreview, closePreview]);

  useEffect(() => {
    const controller = new AbortController();

    const fetchGalleryFromEndpoint = async (url, withCategory = false) => {
      const endpointUrl = `${url}?${
        withCategory
          ? new URLSearchParams({
              category: "smile-gallery",
              limit: "8",
              sortBy: "order",
              sortOrder: "asc",
            }).toString()
          : new URLSearchParams({
              limit: "8",
              sortBy: "order",
              sortOrder: "asc",
            }).toString()
      }`;

      const headers = SMILE_GALLERY_API?.token
        ? { Authorization: `Bearer ${SMILE_GALLERY_API.token}` }
        : {};

      const response = await fetch(endpointUrl, {
        signal: controller.signal,
        headers,
      });

      const payload = await response.json();

      if (!response.ok || payload?.success === false) {
        return [];
      }

      const source = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload)
          ? payload
          : [];

      return source
        .map((item, index) => normalizeGalleryItem(item, index))
        .filter((item) => item.images.length > 0)
        .sort((left, right) => {
          const orderDiff = getGalleryOrder(left) - getGalleryOrder(right);
          if (orderDiff !== 0) return orderDiff;
          return getGallerySortTime(left) - getGallerySortTime(right);
        })
        .slice(0, 8);
    };

    const loadSmileGallery = async () => {
      try {
        const directGallery = await fetchGalleryFromEndpoint(
          SMILE_GALLERY_API.url,
          false,
        );
        if (directGallery.length) {
          setApiGalleryItems(directGallery);
          return;
        }

        const testimonialGallery = await fetchGalleryFromEndpoint(
          TESTIMONIALS_API.url,
          true,
        );
        setApiGalleryItems(testimonialGallery);
      } catch (err) {
        if (err.name === "AbortError") return;
      } finally {
        setLoading(false);
      }
    };

    if (SMILE_GALLERY_API?.url) {
      loadSmileGallery();
    } else {
      setLoading(false);
    }

    return () => controller.abort();
  }, []);

  return (
    <>
      <PageHero
        subtitle="Smile Gallery"
        title="Smiles by Dr.K."
        breadcrumb="Smile Gallery"
        image=            "https://galleria-dental-media.s3.eu-north-1.amazonaws.com/uploads/gallery/1eb72c60-88df-4538-a693-8a3d31ce70dc.jpg"
      />

      <section className="bg-[#FBF8F1] py-10">
        <div className="max-w-7xl mx-auto px-6">
          {loading ? (
            <p className="text-center text-[#7D6A42] text-sm tracking-[0.18em] uppercase mt-12">
              Loading smile gallery...
            </p>
          ) : apiGalleryItems.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mb-12">
              {apiGalleryItems.map((g) => (
                <GalleryCard
                  key={g.id}
                  item={g}
                  onOpen={(item) => setActivePreview({ item })}
                />
              ))}
            </div>
          ) : (
            <p className="text-center text-[#7D6A42] text-sm tracking-[0.12em] uppercase mt-12">
              No smile gallery images available from API.
            </p>
          )}
          <div className="text-center mt-2">
            <div className="mt-7">
              <Button
                href="https://smilesbydoctork.com/gallery"
                target="_blank"
                rel="noopener noreferrer"
                className="!normal-case"
              >
                View Full Gallery at Smiles by DrK
              </Button>
            </div>
          </div>
        </div>
      </section>

      {activePreview && previewSlides.length > 0 && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex flex-col"
          role="dialog"
          aria-modal="true"
          aria-label={activePreview.item.label}
          onClick={closePreview}
        >
          <div
            className="flex items-center justify-between px-6 py-4 flex-shrink-0"
            onClick={(event) => event.stopPropagation()}
          >
            <div>
              <div className="text-xs tracking-[0.3em] uppercase text-white/40 font-mono">
                {activePreviewIndex + 1} / {previewSlides.length}
              </div>
              <div className="text-sm text-white/80 mt-0.5">
                {activePreview.item.label}
              </div>
            </div>

            <button
              onClick={closePreview}
              className="w-9 h-9 border border-white/20 flex items-center justify-center text-white hover:border-[#B79A68] hover:text-[#B79A68] transition-colors"
              aria-label="Close"
            >
              <X size={16} strokeWidth={1.4} />
            </button>
          </div>

          <div
            className="flex-1 overflow-hidden flex items-center justify-center relative"
            onClick={(event) => event.stopPropagation()}
          >
            {previewSlides.length > 1 && (
              <button
                onClick={showPreviousPreview}
                className="fixed left-4 top-1/2 -translate-y-1/2 z-[110] w-11 h-11 border border-white/20 flex items-center justify-center text-white hover:border-[#B79A68] hover:text-[#B79A68] transition-colors bg-black/40 backdrop-blur-sm"
                aria-label="Previous"
              >
                <ChevronLeft size={20} strokeWidth={1.4} />
              </button>
            )}

            <div className="w-full h-full flex items-center justify-center px-16 py-6">
              <img
                src={previewSlides[activePreviewIndex]}
                alt={`${activePreview.item.label} ${activePreviewIndex + 1}`}
                className="max-h-[72vh] w-auto max-w-full object-contain"
                draggable={false}
              />
            </div>

            {previewSlides.length > 1 && (
              <button
                onClick={showNextPreview}
                className="fixed right-4 top-1/2 -translate-y-1/2 z-[110] w-11 h-11 border border-white/20 flex items-center justify-center text-white hover:border-[#B79A68] hover:text-[#B79A68] transition-colors bg-black/40 backdrop-blur-sm"
                aria-label="Next"
              >
                <ChevronRight size={20} strokeWidth={1.4} />
              </button>
            )}
          </div>

          <div
            className="flex-shrink-0 px-4 md:px-6 py-4"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-center mb-3">
              <span className="inline-block px-3 py-1 text-xs tracking-widest uppercase border border-[#B79A68]/40 text-[#B79A68]/70">
                {activePreview.item.source?.category || "Smile Gallery"}
              </span>
            </div>

            {previewSlides.length > 1 && (
              <div className="flex items-center gap-2 overflow-x-auto pb-1 justify-start md:justify-center">
                {previewSlides.map((slide, index) => (
                  <button
                    key={`preview-thumb-${index}`}
                    type="button"
                    onClick={() => setActivePreviewIndex(index)}
                    aria-label={`View image ${index + 1}`}
                    className={`relative w-14 h-14 md:w-16 md:h-16 shrink-0 overflow-hidden border transition-all duration-300 ${
                      index === activePreviewIndex
                        ? "border-[#B79A68] ring-1 ring-[#B79A68]/70"
                        : "border-white/20 hover:border-white/50"
                    }`}
                  >
                    <img
                      src={slide}
                      alt={`${activePreview.item.label} thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                      draggable={false}
                    />
                    {index !== activePreviewIndex && (
                      <div className="absolute inset-0 bg-black/35" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <VideoTestimonialsFilmRoll />
    </>
  );
};

export default SmileGallery;
